/**
 * Google Ads / GTM `purchase` dataLayer for headless `/book` (Mews Booking Engine API).
 * Sojern `brs_book_*` events are unchanged — see `sojernBookDataLayer.ts`.
 *
 * @see user tracking/GTM_GOOGLE_ADS_PURCHASE_SETUP.md (Phase 1)
 */

export const BOOK_PURCHASE_FIRED_STORAGE_PREFIX = "book_purchase_fired:";
export const BOOK_PURCHASE_EMAIL_STORAGE_PREFIX = "book_purchase_email:";
export const BOOK_PURCHASE_CHF_STORAGE_PREFIX = "book_purchase_chf:";

const SETTLEMENT_CURRENCY = "CHF";

export type BookPurchaseAmount = {
  Currency?: string;
  GrossValue?: number;
  NetValue?: number;
  TaxValues?: Array<{ TaxRateCode?: string; Value?: number }>;
};

export type BookPurchaseReservation = {
  Id?: string;
  RoomCategoryId?: string;
  Amount?: BookPurchaseAmount;
};

export type BookPurchasePayment = {
  State?: string;
  ChargeAmount?: BookPurchaseAmount;
  Amount?: BookPurchaseAmount;
};

export type BookPurchaseGroup = {
  Id?: string;
  TotalAmount?: BookPurchaseAmount;
  Reservations?: BookPurchaseReservation[];
  Payments?: BookPurchasePayment[];
};

export type BookPurchaseEmailSource = {
  isBookingOnBehalf: boolean;
  email: string;
  bookerEmail: string;
};

export type BookPurchaseItem = {
  item_id: string;
  item_name: string;
  item_category: "Reservation";
  price: number;
  quantity: 1;
};

export type ResolvedChfGross = {
  value: number;
  tax?: number;
  items: BookPurchaseItem[];
};

type StashedPurchaseEmail = {
  groupId: string;
  sha256: string;
};

type BrowserWindow = Window & {
  dataLayer?: Array<Record<string, unknown>>;
};

const firedGroupIds = new Set<string>();
const inFlightGroupIds = new Set<string>();

function getBrowserWindow(): BrowserWindow | undefined {
  if (typeof globalThis === "undefined") return undefined;
  const w = (globalThis as { window?: BrowserWindow }).window;
  return w;
}

function getSessionStorage(): Storage | null {
  try {
    return getBrowserWindow()?.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function isChfCurrency(code: string | undefined): boolean {
  return code?.trim().toUpperCase() === SETTLEMENT_CURRENCY;
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function roundChf(value: number): number {
  return Math.round(value * 100) / 100;
}

function taxFromAmount(amount: BookPurchaseAmount | undefined): number | undefined {
  if (!amount || !isChfCurrency(amount.Currency)) return undefined;
  const gross = finiteNumber(amount.GrossValue);
  const net = finiteNumber(amount.NetValue);
  if (gross != null && net != null) {
    const tax = roundChf(gross - net);
    if (Number.isFinite(tax) && tax >= 0) return tax;
  }
  if (Array.isArray(amount.TaxValues)) {
    const sum = amount.TaxValues.reduce((acc, row) => {
      const v = finiteNumber(row?.Value);
      return acc + (v ?? 0);
    }, 0);
    if (Number.isFinite(sum) && sum >= 0) return roundChf(sum);
  }
  return undefined;
}

function chfGross(amount: BookPurchaseAmount | undefined): number | undefined {
  if (!amount || !isChfCurrency(amount.Currency)) return undefined;
  const gross = finiteNumber(amount.GrossValue);
  if (gross == null || gross <= 0) return undefined;
  return gross;
}

function chfGrossFromChargedPayments(group: BookPurchaseGroup): number | undefined {
  const charged = group.Payments?.find((payment) => payment?.State === "Charged");
  return chfGross(charged?.ChargeAmount) ?? chfGross(charged?.Amount);
}

/**
 * Prefer group TotalAmount when it is CHF; otherwise sum reservation Amounts in CHF.
 * After Mews return, getReservationGroup often omits those fields — use Charged Payments next.
 * Never falls back to display-currency UI totals.
 */
export function resolveChfGrossFromReservationGroup(
  group: BookPurchaseGroup,
  itemNameByRoomCategoryId?: Record<string, string>,
): ResolvedChfGross | null {
  const reservations = group.Reservations ?? [];
  const totalChf = chfGross(group.TotalAmount);
  let reservationSum = 0;
  let reservationSumHasValue = false;
  for (const reservation of reservations) {
    const gross = chfGross(reservation.Amount);
    if (gross != null) {
      reservationSum += gross;
      reservationSumHasValue = true;
    }
  }

  const value =
    totalChf ??
    (reservationSumHasValue && reservationSum > 0 ? reservationSum : undefined) ??
    chfGrossFromChargedPayments(group);
  if (value == null) return null;

  const tax = taxFromAmount(group.TotalAmount);
  const items: BookPurchaseItem[] =
    reservations.length > 0
      ? reservations.map((reservation, index) => {
          const categoryId = reservation.RoomCategoryId?.trim();
          const itemId = categoryId || reservation.Id?.trim() || `reservation-${index + 1}`;
          const named = categoryId ? itemNameByRoomCategoryId?.[categoryId]?.trim() : undefined;
          const price = chfGross(reservation.Amount) ?? (reservations.length === 1 ? value : 0);
          return {
            item_id: itemId,
            item_name: named || "Reservation",
            item_category: "Reservation",
            price,
            quantity: 1,
          };
        })
      : [
          {
            item_id: group.Id?.trim() || "reservation-group",
            item_name: "Reservation",
            item_category: "Reservation",
            price: value,
            quantity: 1,
          },
        ];

  const resolved: ResolvedChfGross = { value, items };
  if (tax != null) resolved.tax = tax;
  return resolved;
}

export function normalizeEmailForHash(email: string): string {
  return email.trim().toLowerCase();
}

/** Booker email when booking on behalf; otherwise guest email. */
export function purchaseEmailFromGuestDetails(form: BookPurchaseEmailSource): string | null {
  const raw = form.isBookingOnBehalf ? form.bookerEmail : form.email;
  const trimmed = raw?.trim();
  return trimmed ? trimmed : null;
}

export async function sha256Hex(value: string): Promise<string | null> {
  try {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) return null;
    const digest = await subtle.digest("SHA-256", new TextEncoder().encode(value));
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

function emailStorageKey(reservationGroupId: string): string {
  return `${BOOK_PURCHASE_EMAIL_STORAGE_PREFIX}${reservationGroupId}`;
}

function firedStorageKey(reservationGroupId: string): string {
  return `${BOOK_PURCHASE_FIRED_STORAGE_PREFIX}${reservationGroupId}`;
}

function chfStorageKey(reservationGroupId: string): string {
  return `${BOOK_PURCHASE_CHF_STORAGE_PREFIX}${reservationGroupId}`;
}

export function hasBookPurchaseFired(reservationGroupId: string): boolean {
  const id = reservationGroupId.trim();
  if (!id) return false;
  if (firedGroupIds.has(id)) return true;
  try {
    return getSessionStorage()?.getItem(firedStorageKey(id)) === "1";
  } catch {
    return false;
  }
}

function markBookPurchaseFired(reservationGroupId: string): void {
  firedGroupIds.add(reservationGroupId);
  try {
    getSessionStorage()?.setItem(firedStorageKey(reservationGroupId), "1");
  } catch {
    // ignore quota / private mode
  }
}

export function readStashedPurchaseEmailHash(reservationGroupId: string): string | null {
  const id = reservationGroupId.trim();
  if (!id) return null;
  try {
    const raw = getSessionStorage()?.getItem(emailStorageKey(id));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StashedPurchaseEmail;
    const hash = parsed?.sha256?.trim();
    if (!hash) return null;
    if (parsed.groupId && parsed.groupId !== id) return null;
    return hash;
  } catch {
    return null;
  }
}

export function clearStashedPurchaseEmailHash(reservationGroupId: string): void {
  const id = reservationGroupId.trim();
  if (!id) return;
  try {
    getSessionStorage()?.removeItem(emailStorageKey(id));
  } catch {
    // ignore
  }
}

function writeStashedPurchaseChfSnapshot(reservationGroupId: string, resolved: ResolvedChfGross): void {
  try {
    getSessionStorage()?.setItem(chfStorageKey(reservationGroupId), JSON.stringify(resolved));
  } catch {
    // ignore
  }
}

export function stashBookPurchaseChfSnapshot(
  reservationGroupId: string,
  group: BookPurchaseGroup,
  itemNameByRoomCategoryId?: Record<string, string>,
): void {
  const id = reservationGroupId.trim();
  if (!id) return;
  const resolved = resolveChfGrossFromReservationGroup(group, itemNameByRoomCategoryId);
  if (!resolved) return;
  writeStashedPurchaseChfSnapshot(id, resolved);
}

/** Stash settlement CHF when the reservation group omits TotalAmount (typical before 3DS). */
export function stashBookPurchaseChfAmount(
  reservationGroupId: string,
  grossValue: number,
  currency: string,
): void {
  const id = reservationGroupId.trim();
  if (!id || !isChfCurrency(currency)) return;
  const value = finiteNumber(grossValue);
  if (value == null || value <= 0) return;
  writeStashedPurchaseChfSnapshot(id, {
    value,
    items: [
      {
        item_id: id,
        item_name: "Reservation",
        item_category: "Reservation",
        price: value,
        quantity: 1,
      },
    ],
  });
}

export function readStashedPurchaseChfSnapshot(reservationGroupId: string): ResolvedChfGross | null {
  const id = reservationGroupId.trim();
  if (!id) return null;
  try {
    const raw = getSessionStorage()?.getItem(chfStorageKey(id));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ResolvedChfGross;
    if (typeof parsed?.value !== "number" || !Number.isFinite(parsed.value) || parsed.value <= 0) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearStashedPurchaseChfSnapshot(reservationGroupId: string): void {
  const id = reservationGroupId.trim();
  if (!id) return;
  try {
    getSessionStorage()?.removeItem(chfStorageKey(id));
  } catch {
    // ignore
  }
}

/**
 * SHA-256 the email and write `{ groupId, sha256 }` before 3DS / hosted checkout.
 * No-ops when email is empty; never writes plaintext.
 */
export async function stashBookPurchaseEmailHash(
  reservationGroupId: string,
  email: string | null | undefined,
): Promise<void> {
  const id = reservationGroupId.trim();
  const normalized = email ? normalizeEmailForHash(email) : "";
  if (!id || !normalized) return;
  const sha256 = await sha256Hex(normalized);
  if (!sha256) return;
  try {
    const payload: StashedPurchaseEmail = { groupId: id, sha256 };
    getSessionStorage()?.setItem(emailStorageKey(id), JSON.stringify(payload));
  } catch {
    // ignore
  }
}

function initDataLayer(win: BrowserWindow): Array<Record<string, unknown>> {
  if (!Array.isArray(win.dataLayer)) {
    win.dataLayer = [];
  }
  return win.dataLayer;
}

/** GA4 hygiene only. Do not also null Ads keys here — GTM reads those after this push. */
function clearEcommerceObject(dataLayer: Array<Record<string, unknown>>): void {
  dataLayer.push({ ecommerce: undefined });
}

export type PushBookPurchaseEventArgs = {
  reservationGroupId: string;
  group: BookPurchaseGroup;
  itemNameByRoomCategoryId?: Record<string, string>;
  /** Live guest/booker email; used when no stashed hash exists (on-site authorized). */
  email?: string | null;
};

/**
 * Pushes one GA4-shaped `purchase` event. Returns false when CHF cannot be resolved,
 * the group was already sent, or the dataLayer is unavailable.
 * Never includes plaintext email.
 */
export async function pushBookPurchaseEvent(args: PushBookPurchaseEventArgs): Promise<boolean> {
  const reservationGroupId = args.reservationGroupId.trim();
  if (!reservationGroupId) return false;
  if (hasBookPurchaseFired(reservationGroupId) || inFlightGroupIds.has(reservationGroupId)) {
    return false;
  }

  const resolved =
    resolveChfGrossFromReservationGroup(args.group, args.itemNameByRoomCategoryId) ??
    readStashedPurchaseChfSnapshot(reservationGroupId);
  if (!resolved) {
    if (import.meta.env?.DEV) {
      console.debug("[book/purchase] skipped", { reservationGroupId, reason: "no-chf" });
    }
    return false;
  }

  const win = getBrowserWindow();
  if (!win) return false;

  inFlightGroupIds.add(reservationGroupId);
  try {
    let sha256 = readStashedPurchaseEmailHash(reservationGroupId);
    if (!sha256 && args.email) {
      sha256 = await sha256Hex(normalizeEmailForHash(args.email));
    }

    if (hasBookPurchaseFired(reservationGroupId)) return false;

    const dataLayer = initDataLayer(win);
    clearEcommerceObject(dataLayer);

    const ecommerce: Record<string, unknown> = {
      transaction_id: reservationGroupId,
      currency: SETTLEMENT_CURRENCY,
      value: resolved.value,
      items: resolved.items,
    };
    if (resolved.tax != null) ecommerce.tax = resolved.tax;

    const payload: Record<string, unknown> = {
      event: "purchase",
      ecommerce,
      transaction_id: reservationGroupId,
      value: resolved.value,
      currency: SETTLEMENT_CURRENCY,
    };
    if (sha256) {
      payload.user_data = { sha256_email_address: sha256 };
    }

    dataLayer.push(payload);
    clearEcommerceObject(dataLayer);

    markBookPurchaseFired(reservationGroupId);
    clearStashedPurchaseEmailHash(reservationGroupId);
    clearStashedPurchaseChfSnapshot(reservationGroupId);

    if (import.meta.env?.DEV) {
      console.debug("[book/purchase]", {
        transaction_id: reservationGroupId,
        value: resolved.value,
        currency: SETTLEMENT_CURRENCY,
        has_email_hash: Boolean(sha256),
        item_count: resolved.items.length,
      });
    }

    return true;
  } catch {
    return false;
  } finally {
    inFlightGroupIds.delete(reservationGroupId);
  }
}

/** Test-only: clear in-memory fired / in-flight guards. */
export function resetBookPurchaseTrackingForTests(): void {
  firedGroupIds.clear();
  inFlightGroupIds.clear();
}
